import React, { useEffect, useRef, useState } from "react";
import {
  FiCheck,
  FiCheckCircle,
  FiChevronRight,
  FiExternalLink,
  FiFileText,
  FiInfo,
  FiLock,
  FiRefreshCw,
  FiShield,
  FiUsers,
  FiX,
  FiZap,
} from "react-icons/fi";
import DashboardLayout from "../../UI/Layout/DashboardLayout";
import {
  checkContactListStatus,
  connectFacebook,
  disconnectFacebook,
  fetchFacebookAccountInfo,
  fetchFacebookLeadForms,
} from "./FacebookApi";
import styles from "./Facebook.module.css";

const facebookpng = "/img/facebook.png";

const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;
const FACEBOOK_AUTH_ENDPOINT = process.env.REACT_APP_FACEBOOK_AUTH_ENDPOINT;
const FACEBOOK_REDIRECT_URI = process.env.REACT_APP_FACEBOOK_REDIRECT_URI;
const FACEBOOK_PERMISSION = process.env.REACT_APP_FACEBOOK_PERMISSION;

const FACEBOOK_ROUTE = "/dashboard/facebook";

const CONTACT_LIST_ROUTE = "/dashboard/contact-list";
// const CONTACT_LIST_CREATE_ROUTE = "/dashboard/contact-list?create=true";

const formatSmartTime = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 5) return "Just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
};

const Facebook = () => {
  const loginData = JSON.parse(localStorage.getItem("login"));
  const userId = loginData?.user?.userId;

  // ---- Connection state ----
  const [facebookConnected, setFacebookConnected] = useState(
    localStorage.getItem("facebook_connected") === "true",
  );
  const [facebookAccount, setFacebookAccount] = useState(() => {
    const saved = localStorage.getItem("facebookAccount");
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch (error) {
      return null;
    }
  });
  const [lastSyncedAt, setLastSyncedAt] = useState(() =>
    localStorage.getItem("facebookLastSyncedAt"),
  );

  // ---- OAuth / modal state ----
  const [oauthLoading, setOauthLoading] = useState(false);
  const [showPostOAuthModal, setShowPostOAuthModal] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);

  // ---- Lead forms state ----
  const [fetchingLeadForms, setFetchingLeadForms] = useState(false);
  const [leadForms, setLeadForms] = useState([]);
  const [fetchError, setFetchError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // ---- Contact List dependency state ----
  // null = not checked yet, true/false = known result.
  const [hasContactList, setHasContactList] = useState(null);
  const [checkingContactList, setCheckingContactList] = useState(false);
  const [showContactInfoPopover, setShowContactInfoPopover] = useState(false);
  const infoPopoverRef = useRef(null);

  // ---- Disconnect state ----
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // ---- Toast-style banners ----
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(""), 3500);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!errorMessage) return undefined;
    const timer = setTimeout(() => setErrorMessage(""), 4500);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  useEffect(() => {
    if (!showContactInfoPopover) return undefined;
    const handleClickOutside = (event) => {
      if (
        infoPopoverRef.current &&
        !infoPopoverRef.current.contains(event.target)
      ) {
        setShowContactInfoPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showContactInfoPopover]);

  // ---------------------------------------------------------------------
  // Persistence helpers
  // ---------------------------------------------------------------------
  const markFacebookConnected = () => {
    setFacebookConnected(true);
    localStorage.setItem("facebook_connected", "true");
  };

  const saveFacebookAccount = (account) => {
    if (!account) return;
    setFacebookAccount(account);
    localStorage.setItem("facebookAccount", JSON.stringify(account));
  };

  const touchLastSynced = () => {
    const now = new Date().toISOString();
    setLastSyncedAt(now);
    localStorage.setItem("facebookLastSyncedAt", now);
  };

  const clearFacebookConnection = () => {
    setFacebookConnected(false);
    setFacebookAccount(null);
    setLeadForms([]);
    setHasContactList(null);
    setLastSyncedAt(null);
    localStorage.removeItem("facebook_connected");
    localStorage.removeItem("facebookAccount");
    localStorage.removeItem("facebookLastSyncedAt");
  };

  // ---------------------------------------------------------------------
  // Data loaders
  // ---------------------------------------------------------------------
  const loadAccountInfo = async () => {
    if (!userId) return;
    try {
      const result = await fetchFacebookAccountInfo({ userId });
      const account = result?.data || result?.account || null;
      if (account) saveFacebookAccount(account);
    } catch (error) {
      // Non-fatal — the connected badge still shows from localStorage even
      // if the richer account/page details can't be refreshed right now.
    }
  };

  const checkContactList = async () => {
    if (!userId) return;
    try {
      setCheckingContactList(true);
      const result = await checkContactListStatus({ userId });
      const contactLists = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.contactLists)
        ? result.contactLists
        : [];
      const flagged =
        result?.hasContactList !== undefined
          ? Boolean(result.hasContactList)
          : contactLists.length > 0;
      setHasContactList(flagged);
    } catch (error) {
      // If the check itself fails, don't block the page on a guess —
      // surface nothing and let the lead-forms fetch/error state speak.
      setHasContactList(null);
    } finally {
      setCheckingContactList(false);
    }
  };

  const loadLeadForms = async ({ silent = false } = {}) => {
    if (!userId || !facebookConnected) return;

    setFetchError("");
    setSessionExpired(false);
    if (!silent) setFetchingLeadForms(true);

    try {
      const result = await fetchFacebookLeadForms({ userId });
      const forms = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.forms)
        ? result.forms
        : [];
      setLeadForms(forms);
      touchLastSynced();
      if (!silent) setSuccessMessage("Facebook lead forms updated successfully.");
    } catch (error) {
      const message = error?.message || "";
      if (/expired|reauthor|invalid token|unauthor/i.test(message)) {
        setSessionExpired(true);
      } else {
        setFetchError(
          message || "We couldn't retrieve your Facebook lead forms right now.",
        );
      }
      if (!silent) {
        setErrorMessage("Unable to fetch Facebook lead forms. Please try again.");
      }
    } finally {
      if (!silent) setFetchingLeadForms(false);
    }
  };

  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const connected = params.get("connected");
    const code = params.get("code");
    const callbackError = params.get("error");

    if (connected === "true") {
      markFacebookConnected();
      setShowPostOAuthModal(true);
      window.history.replaceState({}, document.title, FACEBOOK_ROUTE);
    }

    if (callbackError) {
      setConnectionFailed(true);
      setErrorMessage(decodeURIComponent(callbackError));
      window.history.replaceState({}, document.title, FACEBOOK_ROUTE);
    }

    if (code) {
      if (!userId) return;

      connectFacebook({ userId, code })
        .then((result) => {
          markFacebookConnected();
          const account = result?.data || result?.account || null;
          if (account) saveFacebookAccount(account);
          setShowPostOAuthModal(true);
          window.history.replaceState(
            {},
            document.title,
            `${FACEBOOK_ROUTE}?connected=true`,
          );
        })
        .catch((error) => {
          setConnectionFailed(true);
          setErrorMessage(error.message || "Unable to connect Facebook.");
          window.history.replaceState({}, document.title, FACEBOOK_ROUTE);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!facebookConnected || !userId) return;
    loadAccountInfo();
    checkContactList();
    loadLeadForms({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facebookConnected, userId]);

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------
  const handleConnectFacebook = () => {
    setErrorMessage("");
    setConnectionFailed(false);

    if (!userId) {
      setErrorMessage("Unable to find the logged-in user ID.");
      return;
    }
    if (!FACEBOOK_APP_ID || !FACEBOOK_AUTH_ENDPOINT || !FACEBOOK_REDIRECT_URI) {
      setErrorMessage("Facebook OAuth configuration is missing.");
      return;
    }

    try {
      setOauthLoading(true);

      const authUrl = `${FACEBOOK_AUTH_ENDPOINT}?client_id=${encodeURIComponent(
        FACEBOOK_APP_ID,
      )}&redirect_uri=${encodeURIComponent(
        FACEBOOK_REDIRECT_URI,
      )}&response_type=code&scope=${encodeURIComponent(
        FACEBOOK_PERMISSION,
      )}&state=${encodeURIComponent(userId)}`;

      window.location.assign(authUrl);
    } catch (error) {
      setOauthLoading(false);
      setErrorMessage("Facebook authorization URL is invalid.");
    }
  };

  const handleContinueHere = () => {
    setShowPostOAuthModal(false);
    checkContactList();
    loadLeadForms({ silent: true });
  };

  const handleGoToContactList = () => {
    setShowPostOAuthModal(false);
    window.location.assign(CONTACT_LIST_ROUTE);
  };

  const handleRefresh = async () => {
    setSyncing(true);
    await Promise.all([checkContactList(), loadLeadForms()]);
    setSyncing(false);
  };

  const handleConfirmDisconnect = async () => {
    if (!userId) {
      setErrorMessage("Unable to find the logged-in user ID.");
      return;
    }

    try {
      setDisconnecting(true);
      await disconnectFacebook({ userId });
      clearFacebookConnection();
      setShowDisconnectModal(false);
      setSuccessMessage("Facebook account disconnected.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to disconnect Facebook.");
    } finally {
      setDisconnecting(false);
    }
  };

  const pageName = facebookAccount?.pageName || facebookAccount?.name || null;

  return (
    <DashboardLayout>
      <main className="content">
        <div className={styles.page}>
          {/* <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <Link to="/dashboard">Dashboard</Link>
            <FiChevronRight className={styles.breadcrumbSep} />
            <span className={styles.breadcrumbCurrent}>Facebook Leads</span>
          </nav> */}

          <div className={styles.header}>
            <div className={styles.brand}>
              <img src={facebookpng} alt="Facebook" className={styles.logo} />
              <div>
                <div className={styles.brandTitleRow}>
                  <h1>Facebook Leads</h1>
                  {facebookConnected ? (
                    <span
                      className={`${styles.statusBadge} ${styles.statusConnected}`}
                    >
                      <span className={styles.statusDot} />
                      Connected
                    </span>
                  ) : (
                    <span
                      className={`${styles.statusBadge} ${styles.statusDisconnected}`}
                    >
                      <span className={styles.statusDot} />
                      Not Connected
                    </span>
                  )}
                </div>
                <p>
                  Connect your Facebook account to securely retrieve and
                  manage lead form submissions from your Facebook Pages.
                </p>
              </div>
            </div>

            {facebookConnected && (
              <div className={styles.headerActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={handleRefresh}
                  disabled={syncing || fetchingLeadForms}
                >
                  <FiRefreshCw className={syncing ? styles.spin : undefined} />
                  {syncing ? "Syncing..." : "Refresh"}
                </button>
                <button
                  type="button"
                  className={styles.btnDanger}
                  onClick={() => setShowDisconnectModal(true)}
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>

          {successMessage && (
            <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status">
              <FiCheckCircle />
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              <FiX />
              {errorMessage}
            </div>
          )}

          {/* ---------------- Disconnected / onboarding state ---------------- */}
          {!facebookConnected && (
            <div className={styles.connectionCard}>
              <div className={styles.connectionIconWrap}>
                <img src={facebookpng} alt="" />
              </div>
              <h2>Connect your Facebook Account</h2>
              <p>
                Link a Facebook Page to automatically pull in Lead Form
                submissions and route them into your Contact Lists for
                follow-up.
              </p>

              {connectionFailed && (
                <p style={{ color: "#b91c1c", fontWeight: 500 }}>
                  Facebook Connection Failed — we couldn't connect your
                  Facebook account. Please try again.
                </p>
              )}

              <button
                type="button"
                className={styles.connectButton}
                onClick={handleConnectFacebook}
                disabled={oauthLoading}
              >
                {oauthLoading && <span className={styles.spinner} />}
                {oauthLoading ? "Connecting to Facebook..." : "Connect Facebook"}
              </button>

              <span className={styles.securityNote}>
                <FiLock />
                Your Facebook credentials are handled securely through
                Facebook OAuth.
              </span>

              <div className={styles.benefitsGrid}>
                <div className={styles.benefitItem}>
                  <FiZap />
                  <strong>Automatic sync</strong>
                  <span>New lead form submissions are pulled in as they come.</span>
                </div>
                <div className={styles.benefitItem}>
                  <FiFileText />
                  <strong>All your forms, in one place</strong>
                  <span>See every connected Page's Lead Ads forms together.</span>
                </div>
                <div className={styles.benefitItem}>
                  <FiShield />
                  <strong>Secure by design</strong>
                  <span>Access is granted only through Facebook's official OAuth flow.</span>
                </div>
              </div>
            </div>
          )}

          {/* ---------------- Post-OAuth "Facebook Connected" modal ---------------- */}
          {showPostOAuthModal && (
            <div className={styles.modalOverlay} role="dialog" aria-modal="true">
              <div className={styles.modal}>
                <div className={styles.modalIconsRow}>
                  <span className={styles.modalSuccessIcon}>
                    <FiCheck />
                  </span>
                  <span className={styles.modalFbIcon}>
                    <img src={facebookpng} alt="" />
                  </span>
                </div>
                <h2>Facebook Account Connected</h2>
                <p>
                  Your Facebook account has been successfully connected. How
                  would you like to continue?
                </p>

                <div className={styles.modalOptions}>
                  <button
                    type="button"
                    className={`${styles.modalOption} ${styles.modalOptionPrimary}`}
                    onClick={handleContinueHere}
                  >
                    <span className={styles.modalOptionIcon}>
                      <FiChevronRight />
                    </span>
                    <span className={styles.modalOptionBody}>
                      <strong>Continue Here</strong>
                      <span>
                        Stay on Facebook Leads and fetch your connected lead
                        forms.
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    className={styles.modalOption}
                    onClick={handleGoToContactList}
                  >
                    <span className={styles.modalOptionIcon}>
                      <FiUsers />
                    </span>
                    <span className={styles.modalOptionBody}>
                      <strong>Go to Contact List</strong>
                      <span>
                        Manage or create the contact list that will receive
                        your Facebook leads.
                      </span>
                    </span>
                  </button>
                </div>

                <p className={styles.modalFooterNote}>
                  You can change your selection later from this page.
                </p>
              </div>
            </div>
          )}

          {/* ---------------- Connected state ---------------- */}
          {facebookConnected && (
            <>
              <div className={styles.section}>
                <div className={styles.connectedCard}>
                  <div className={styles.connectedInfo}>
                    <div className={styles.connectedIconWrap}>
                      <img src={facebookpng} alt="" />
                    </div>
                    <div className={styles.connectedText}>
                      <strong>Connected Facebook Account</strong>
                      {pageName && <span>Facebook Page: {pageName}</span>}
                      <span>
                        Last synced:{" "}
                        {lastSyncedAt ? formatSmartTime(lastSyncedAt) : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Session/token expired */}
              {sessionExpired && (
                <div className={styles.tableCard}>
                  <div className={styles.emptyState}>
                    <div className={`${styles.emptyIconWrap} ${styles.emptyIconWarn}`}>
                      <FiLock />
                    </div>
                    <h3>Facebook Connection Expired</h3>
                    <p>Your Facebook connection needs to be renewed.</p>
                    <div className={styles.emptyActions}>
                      <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={handleConnectFacebook}
                      >
                        Reconnect Facebook
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Case A — No Contact List (blocks lead form display) */}
              {!sessionExpired && hasContactList === false && (
                <div className={styles.tableCard}>
                  <div className={styles.emptyState}>
                    <div className={`${styles.emptyIconWrap} ${styles.emptyIconWarn}`}>
                      <FiUsers />
                    </div>
                    <h3>No Contact List Found</h3>
                    <p>
                      Facebook is connected successfully, but you need to
                      create a Contact List before Facebook leads can be
                      imported and managed.
                    </p>
                    <div className={styles.emptyActions}>
                      {/* <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={() =>
                          window.location.assign(CONTACT_LIST_CREATE_ROUTE)
                        }
                      >
                        Create Contact List
                      </button> */}
                      <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() =>
                          window.location.assign(CONTACT_LIST_ROUTE)
                        }
                      >
                        Go to Contact Lists <FiExternalLink />
                      </button>
                    </div>

                    <div className={styles.infoPopover} ref={infoPopoverRef}>
                      <button
                        type="button"
                        className={styles.infoTrigger}
                        onClick={() => setShowContactInfoPopover((v) => !v)}
                        aria-expanded={showContactInfoPopover}
                      >
                        <FiInfo /> Why do I need a Contact List?
                      </button>
                      {showContactInfoPopover && (
                        <div className={styles.infoPopoverPanel} role="dialog">
                          <strong>Why is a Contact List required?</strong>
                          <p>
                            Contact Lists are used as the destination for
                            Facebook lead data. Create a Contact List first
                            so incoming leads can be organized and managed
                            within the platform.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Lead Forms section — only once we know a Contact List exists
                  (or the check itself is inconclusive, so we don't block the
                  user indefinitely on a failed dependency check). */}
              {!sessionExpired && hasContactList !== false && (
                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>Lead Forms</h2>

                  <div className={styles.tableCard}>
                    {fetchingLeadForms || checkingContactList ? (
                      <>
                        {[...Array(4)].map((_, index) => (
                          <div className={styles.skeletonRow} key={index}>
                            <span
                              className={styles.skeletonBlock}
                              style={{ width: "26%" }}
                            />
                            <span
                              className={styles.skeletonBlock}
                              style={{ width: "18%" }}
                            />
                            <span
                              className={styles.skeletonBlock}
                              style={{ width: "10%" }}
                            />
                            <span
                              className={styles.skeletonBlock}
                              style={{ width: "16%" }}
                            />
                            <span
                              className={styles.skeletonBlock}
                              style={{ width: "10%" }}
                            />
                          </div>
                        ))}
                      </>
                    ) : fetchError ? (
                      <div className={styles.emptyState}>
                        <div
                          className={`${styles.emptyIconWrap} ${styles.emptyIconWarn}`}
                        >
                          <FiX />
                        </div>
                        <h3>Unable to Fetch Lead Forms</h3>
                        <p>{fetchError}</p>
                        <div className={styles.emptyActions}>
                          <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={() => loadLeadForms()}
                          >
                            Retry
                          </button>
                        </div>
                      </div>
                    ) : leadForms.length === 0 ? (
                      // Case B — Contact List exists but no lead forms yet.
                      <div className={styles.emptyState}>
                        <div
                          className={`${styles.emptyIconWrap} ${styles.emptyIconNeutral}`}
                        >
                          <FiFileText />
                        </div>
                        <h3>No Lead Forms Found</h3>
                        <p>
                          We couldn't find any lead forms associated with
                          your connected Facebook Pages.
                        </p>
                        <div className={styles.emptyActions}>
                          <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={handleRefresh}
                          >
                            <FiRefreshCw /> Refresh
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={styles.tableScroll}>
                          <table className={styles.table}>
                            <thead>
                              <tr>
                                <th>Form Name</th>
                                <th>Facebook Page</th>
                                <th style={{ textAlign: "right" }}>Leads</th>
                                <th>Last Updated</th>
                                <th>Status</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {leadForms.map((form, index) => {
                                const isActive =
                                  String(
                                    form.status || form.formStatus || "",
                                  ).toLowerCase() === "active";
                                return (
                                  <tr key={form.id || form.formId || index}>
                                    <td className={styles.formNameCell}>
                                      {form.formName || form.name || "—"}
                                    </td>
                                    <td>
                                      {form.pageName || form.page || "—"}
                                    </td>
                                    <td className={styles.numCell}>
                                      {form.leadsCount ?? form.leads ?? 0}
                                    </td>
                                    <td>
                                      {formatSmartTime(
                                        form.updatedAt || form.lastUpdated,
                                      ) || "—"}
                                    </td>
                                    <td>
                                      <span
                                        className={
                                          isActive
                                            ? styles.pillActive
                                            : styles.pillInactive
                                        }
                                      >
                                        {isActive ? "Active" : "Inactive"}
                                      </span>
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className={styles.rowAction}
                                      >
                                        View Leads
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile card view */}
                        <div className={styles.leadFormCards}>
                          {leadForms.map((form, index) => {
                            const isActive =
                              String(
                                form.status || form.formStatus || "",
                              ).toLowerCase() === "active";
                            return (
                              <div
                                className={styles.leadFormCard}
                                key={form.id || form.formId || index}
                              >
                                <div className={styles.leadFormCardTop}>
                                  <strong>{form.formName || form.name || "—"}</strong>
                                  <span
                                    className={
                                      isActive
                                        ? styles.pillActive
                                        : styles.pillInactive
                                    }
                                  >
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </div>
                                <div className={styles.leadFormCardRow}>
                                  <span>Page</span>
                                  <span>{form.pageName || form.page || "—"}</span>
                                </div>
                                <div className={styles.leadFormCardRow}>
                                  <span>Leads</span>
                                  <span>{form.leadsCount ?? form.leads ?? 0}</span>
                                </div>
                                <div className={styles.leadFormCardRow}>
                                  <span>Updated</span>
                                  <span>
                                    {formatSmartTime(
                                      form.updatedAt || form.lastUpdated,
                                    ) || "—"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ---------------- Disconnect confirmation modal ---------------- */}
          {showDisconnectModal && (
            <div className={styles.modalOverlay} role="dialog" aria-modal="true">
              <div className={styles.modal}>
                <h2>Disconnect Facebook?</h2>
                <p>
                  Disconnecting Facebook will stop this account from
                  fetching new lead data. Existing lead data will remain
                  unchanged.
                </p>
                <div className={styles.modalActionsRow}>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={() => setShowDisconnectModal(false)}
                    disabled={disconnecting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.btnDangerSolid}
                    onClick={handleConfirmDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting && <span className={styles.spinner} />}
                    {disconnecting ? "Disconnecting..." : "Disconnect"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </DashboardLayout>
  );
};

export default Facebook;